import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useEffect, useState, useRef } from 'react';
import { getRelativeTime } from '../lib/utils/datetime';
import {
  IconPaperclip,
  IconChevronDown,
  IconExternalLink,
  IconStar,
  IconDownload,
  IconFileText,
  IconPhoto,
  IconFile,
  IconChevronRight,
  IconCopy,
  IconTrash,
  IconArchive,
  IconAlertTriangle,
  IconMail,
  IconBellOff,
  IconArrowBackUp,
  IconSend,
  IconEye,
  IconFileTypePdf,
  IconFileWord,
  IconFileSpreadsheet,
  IconPresentation,
  IconFileZip,
  IconVideo,
  IconMusic,
  IconCode,
} from '@tabler/icons-react';
import { cn } from "@/lib/utils";
import { LabelEditor } from './label-editor';
import { AIAnalysisPopover } from './ai-analysis-popover';
import { EmailAnalysis, getAnalysisLabels } from '@/lib/llm/types';
import { EmailAttachment, ThreadMessage, EmailThread } from '@/types/email';
import { UnsubscribeValue } from '@/types/email-list.types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  getEmailThreadFromCFWorker,
  downloadAttachmentFromCFWorker,
  openAttachmentFromCFWorker,
  singleEmailAction,
  bulkEmailAction,
  handleUnsubscribe,
  sendReply,
} from '@/lib/client/email';
import {
  cacheSavedDriveAttachment,
  getCachedSavedDriveAttachment,
  DriveClientError,
  saveAttachmentToDrive,
  startDriveConnectForAttachment,
} from '@/lib/client/drive';
import { useCFAuth } from '@/lib/auth/cf-auth-context';
import { toast } from 'sonner';

const EMPTY_ATTACHMENTS: EmailAttachment[] = [];

function areSavedDriveAttachmentsEqual(
  current: Record<string, { webViewLink: string | null }>,
  next: Record<string, { webViewLink: string | null }>
): boolean {
  const currentKeys = Object.keys(current);
  const nextKeys = Object.keys(next);

  if (currentKeys.length !== nextKeys.length) {
    return false;
  }

  return currentKeys.every((key) => current[key]?.webViewLink === next[key]?.webViewLink);
}

interface UserLabel {
  id: string;
  name: string;
  color: string;
}

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: {
    id?: string;
    threadId?: string;
    subject?: string;
    sender?: string;
    receivedAt?: string;
    content?: string;
    mimeType?: string;
    headers?: Record<string, string>;
    isRead?: boolean;
    isStarred?: boolean;
    hasAttachments?: boolean;
    aiAnalysis?: EmailAnalysis;
    userLabels?: UserLabel[];
    attachments?: EmailAttachment[];
  } | null;
  onEmailRead?: (emailId: string) => void;
  onLabelChange?: (emailId: string, oldLabel: string, newLabel: string) => void;
  onStarToggle?: (emailId: string, starred: boolean) => void;
  onAnalysisUpdate?: (emailId: string, analysis: EmailAnalysis) => void;
  onEmailAction?: (emailId: string, action: 'delete' | 'archive' | 'spam' | 'unsubscribe') => void;
}

function getGmailLink(email: { id?: string; threadId?: string }): string | null {
  if (email.threadId) {
    return `https://mail.google.com/mail/u/0/#inbox/${email.threadId}`;
  }
  if (email.id) {
    return `https://mail.google.com/mail/u/0/#inbox/${email.id}`;
  }
  return null;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function hasAttachmentExtension(filename: string, extensions: string[]): boolean {
  const normalized = filename.trim().toLowerCase();
  return extensions.some((extension) => normalized.endsWith(extension));
}

function getAttachmentVisual(attachment: EmailAttachment): {
  icon: React.ComponentType<{ className?: string }>;
  className: string;
} {
  const mimeType = attachment.mimeType.toLowerCase();
  const filename = attachment.filename.toLowerCase();

  if (mimeType.startsWith('image/')) {
    return { icon: IconPhoto, className: 'text-sky-600 dark:text-sky-400' };
  }

  if (mimeType.includes('pdf') || hasAttachmentExtension(filename, ['.pdf'])) {
    return { icon: IconFileTypePdf, className: 'text-rose-600 dark:text-rose-400' };
  }

  if (
    mimeType.includes('word')
    || hasAttachmentExtension(filename, ['.doc', '.docx', '.pages', '.rtf'])
  ) {
    return { icon: IconFileWord, className: 'text-blue-600 dark:text-blue-400' };
  }

  if (
    mimeType.includes('sheet')
    || mimeType.includes('excel')
    || mimeType.includes('csv')
    || hasAttachmentExtension(filename, ['.xls', '.xlsx', '.csv', '.tsv', '.numbers'])
  ) {
    return { icon: IconFileSpreadsheet, className: 'text-emerald-600 dark:text-emerald-400' };
  }

  if (
    mimeType.includes('presentation')
    || mimeType.includes('powerpoint')
    || hasAttachmentExtension(filename, ['.ppt', '.pptx', '.key'])
  ) {
    return { icon: IconPresentation, className: 'text-orange-600 dark:text-orange-400' };
  }

  if (
    mimeType.startsWith('video/')
    || hasAttachmentExtension(filename, ['.mp4', '.mov', '.avi', '.mkv', '.webm'])
  ) {
    return { icon: IconVideo, className: 'text-fuchsia-600 dark:text-fuchsia-400' };
  }

  if (
    mimeType.startsWith('audio/')
    || hasAttachmentExtension(filename, ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac'])
  ) {
    return { icon: IconMusic, className: 'text-violet-600 dark:text-violet-400' };
  }

  if (
    mimeType.includes('zip')
    || mimeType.includes('compressed')
    || hasAttachmentExtension(filename, ['.zip', '.rar', '.7z', '.tar', '.gz'])
  ) {
    return { icon: IconFileZip, className: 'text-amber-600 dark:text-amber-400' };
  }

  if (
    mimeType.includes('json')
    || mimeType.includes('xml')
    || mimeType.includes('javascript')
    || mimeType.includes('typescript')
    || mimeType.includes('html')
    || hasAttachmentExtension(filename, ['.json', '.xml', '.js', '.ts', '.tsx', '.jsx', '.html', '.css'])
  ) {
    return { icon: IconCode, className: 'text-slate-600 dark:text-slate-400' };
  }

  if (
    mimeType.startsWith('text/')
    || hasAttachmentExtension(filename, ['.txt', '.md', '.log'])
  ) {
    return { icon: IconFileText, className: 'text-muted-foreground' };
  }

  return { icon: IconFile, className: 'text-muted-foreground' };
}

type EmailAction = 'delete' | 'archive' | 'spam' | 'unsubscribe' | 'mark_unread';

interface ActionButtonProps {
  action: EmailAction;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  hoverClassName?: string;
  onAction: (action: EmailAction) => void;
  disabled: boolean;
  actionInProgress: string | null;
}

function ActionButton({ action, title, icon: Icon, hoverClassName, onAction, disabled, actionInProgress }: ActionButtonProps) {
  return (
    <button
      onClick={() => onAction(action)}
      disabled={disabled}
      className={cn(
        "p-1.5 rounded hover:bg-muted transition-colors",
        actionInProgress === action && "opacity-50",
        hoverClassName
      )}
      title={title}
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

function GoogleDriveIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 -13.5 256 256"
      fill="none"
      className={className}
      aria-hidden="true"
      preserveAspectRatio="xMidYMid"
    >
      <path
        d="M19.3542312 196.033928 30.644172 215.534816C32.9900287 219.64014 36.3622164 222.86588 40.3210929 225.211737 51.6602421 210.818376 59.5534225 199.772864 64.000634 192.075201 68.5137119 184.263529 74.0609657 172.045039 80.6423954 155.41973 62.9064315 153.085282 49.4659974 151.918058 40.3210929 151.918058 31.545465 151.918058 18.1051007 153.085282 0 155.41973 0 159.964996 1.17298825 164.510261 3.51893479 168.615586L19.3542312 196.033928Z"
        fill="#0066DA"
      />
      <path
        d="M215.681443 225.211737C219.64032 222.86588 223.012507 219.64014 225.358364 215.534816L230.050377 207.470615 252.483511 168.615586C254.829368 164.510261 256.002446 159.964996 256.002446 155.41973 237.79254 153.085282 224.376613 151.918058 215.754667 151.918058 206.488712 151.918058 193.072785 153.085282 175.506888 155.41973 182.010479 172.136093 187.484394 184.354584 191.928633 192.075201 196.412073 199.863919 204.329677 210.909431 215.681443 225.211737Z"
        fill="#EA4335"
      />
      <path
        d="M128.001268 73.3111515C141.121182 57.4655263 150.162898 45.2470011 155.126415 36.6555757 159.123121 29.7376196 163.521739 18.6920726 168.322271 3.51893479 164.363395 1.1729583 159.818129 0 155.126415 0H100.876121C96.1841079 0 91.638842 1.31958557 87.6799655 3.51893479 93.7861943 20.9210065 98.9675428 33.3058067 103.224011 40.6733354 107.927832 48.8151881 116.186918 59.6944602 128.001268 73.3111515Z"
        fill="#00832D"
      />
      <path
        d="M175.360141 155.41973H80.6420959L40.3210929 225.211737C44.2799694 227.557893 48.8252352 228.730672 53.5172481 228.730672H202.485288C207.177301 228.730672 211.722567 227.411146 215.681443 225.211737L175.360141 155.41973Z"
        fill="#2684FC"
      />
      <path
        d="M128.001268 73.3111515 87.680265 3.51893479C83.7213885 5.86488134 80.3489013 9.09044179 78.0030446 13.1960654L3.51893479 142.223575C1.17298825 146.329198 0 150.874464 0 155.41973H80.6423954L128.001268 73.3111515Z"
        fill="#00AC47"
      />
      <path
        d="M215.241501 77.7099697 177.999492 13.1960654C175.653635 9.09044179 172.281148 5.86488134 168.322271 3.51893479L128.001268 73.3111515 175.360141 155.41973H255.855999C255.855999 150.874464 254.682921 146.329198 252.337064 142.223575L215.241501 77.7099697Z"
        fill="#FFBA00"
      />
    </svg>
  );
}

export function EmailModal({ isOpen, onClose, email, onEmailRead, onLabelChange, onStarToggle, onAnalysisUpdate, onEmailAction }: EmailModalProps) {
  const { accessToken, user } = useCFAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [thread, setThread] = useState<EmailThread | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [downloadingAttachment, setDownloadingAttachment] = useState<string | null>(null);
  const [viewingAttachment, setViewingAttachment] = useState<string | null>(null);
  const [savingAttachmentKey, setSavingAttachmentKey] = useState<string | null>(null);
  const [savedDriveAttachments, setSavedDriveAttachments] = useState<Record<string, { webViewLink: string | null }>>({});
  const markedAsReadRef = useRef<Set<string>>(new Set());
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [isReplying, setIsReplying] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [archiveAfterReply, setArchiveAfterReply] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const resetReplyComposer = () => {
    setIsReplying(false);
    setReplyBody('');
    setArchiveAfterReply(false);
  };

  useEffect(() => {
    setIframeLoaded(false);
    setShowDetails(false);
    setThread(null);
    setExpandedMessages(new Set());
    setSavedDriveAttachments({});
    resetReplyComposer();
  }, [email?.id]);

  useEffect(() => {
    if (!isReplying) return;

    const frame = requestAnimationFrame(() => {
      replyTextareaRef.current?.focus();
    });

    return () => cancelAnimationFrame(frame);
  }, [isReplying]);

  // Mark email as read when opened - always sync to Gmail
  useEffect(() => {
    async function markAsRead() {
      if (!isOpen || !email?.id || !accessToken) {
        return;
      }
      // Skip if already read or already processed in this session.
      // Set is updated synchronously before the await so concurrent
      // effect re-runs are blocked immediately.
      if (markedAsReadRef.current.has(email.id) || email.isRead) {
        markedAsReadRef.current.add(email.id);
        return;
      }
      markedAsReadRef.current.add(email.id);

      try {
        console.log('[markAsRead] Syncing to Gmail:', email.id);
        const { singleEmailAction } = await import('@/lib/client/email');
        await singleEmailAction(email.id, 'mark_read', accessToken);
        console.log('[markAsRead] Gmail sync success:', email.id);
        onEmailRead?.(email.id);
      } catch (error) {
        markedAsReadRef.current.delete(email.id);
        console.error('[markAsRead] Gmail sync failed:', error);
      }
    }

    markAsRead();
  }, [isOpen, email?.id, email?.isRead, accessToken, onEmailRead]);

  // Load thread if email has a threadId
  useEffect(() => {
    async function loadThread() {
      if (!email?.threadId || !accessToken || !isOpen) return;

      setLoadingThread(true);
      try {
        const threadData = await getEmailThreadFromCFWorker(email.threadId, accessToken);
        // Only show thread view if there are multiple messages
        if (threadData.messages && threadData.messages.length > 1) {
          setThread(threadData);
          // Expand the latest message by default
          const latestId = threadData.messages[threadData.messages.length - 1]?.id;
          if (latestId) {
            setExpandedMessages(new Set([latestId]));
          }
        }
      } catch (error) {
        console.error('Failed to load thread:', error);
      } finally {
        setLoadingThread(false);
      }
    }

    loadThread();
  }, [email?.threadId, accessToken, isOpen]);

  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  const createIframeSrcDoc = (content: string) => `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <base target="_blank">
      <style>
        * { box-sizing: border-box; }
        body {
          margin: 0;
          padding: 20px 24px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          font-size: 14px;
          line-height: 1.7;
          color: ${isDark ? '#d4d4d4' : '#404040'};
          background: transparent;
          word-wrap: break-word;
          overflow-wrap: break-word;
          overflow: visible;
        }
        html, body {
          height: auto;
        }
        a { color: ${isDark ? '#60a5fa' : '#2563eb'}; }
        img { max-width: 100%; height: auto; }
        pre, code { white-space: pre-wrap; word-wrap: break-word; font-size: 13px; }
        table { max-width: 100%; }
        body > * { max-width: 100% !important; }
        blockquote {
          margin: 16px 0;
          padding-left: 16px;
          border-left: 3px solid ${isDark ? '#404040' : '#e5e5e5'};
          color: ${isDark ? '#a3a3a3' : '#737373'};
        }
      </style>
    </head>
    <body>${content}</body>
    </html>
  `;

  const resizeIframeToContent = (iframe: HTMLIFrameElement, minHeight = 140) => {
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      const body = doc.body;
      const docEl = doc.documentElement;
      const height = Math.max(
        body?.scrollHeight || 0,
        docEl?.scrollHeight || 0,
        minHeight
      );
      iframe.style.height = `${height}px`;
    } catch {
      // Ignore sizing errors for cross-origin content
    }
  };

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe) {
      resizeIframeToContent(iframe, 220);
      setTimeout(() => resizeIframeToContent(iframe, 220), 100);
    }
    setTimeout(() => setIframeLoaded(true), 50);
  };

  const getSenderName = (sender?: string) => {
    if (!sender) return 'Unknown';
    return sender.split('<')[0].trim().replace(/['"]/g, '');
  };

  const getSenderEmail = (sender?: string) => {
    if (!sender) return '';
    const matches = sender.match(/<(.+?)>/);
    return matches ? matches[1] : sender;
  };

  const getSenderInitials = (sender?: string) => {
    const name = getSenderName(sender);
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const focusLatestThreadMessage = (threadData: EmailThread) => {
    const latestId = threadData.messages[threadData.messages.length - 1]?.id;
    if (latestId) {
      setExpandedMessages(new Set([latestId]));
    }
  };

  const getRecipientName = (recipient?: string) => {
    if (!recipient) return 'me';
    // Extract first recipient if multiple
    const firstRecipient = recipient.split(',')[0].trim();
    // Get name part (before email in brackets)
    const name = firstRecipient.split('<')[0].trim().replace(/['"]/g, '');
    // If no name, extract username from email
    if (!name || name.includes('@')) {
      const emailMatch = firstRecipient.match(/<(.+?)>/) || [null, firstRecipient];
      const email = emailMatch[1] || firstRecipient;
      return email.split('@')[0];
    }
    return name;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return getRelativeTime(Math.floor(date.getTime() / 1000));
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatThreadDate = (internalDate: string) => {
    const date = new Date(parseInt(internalDate));
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getRecipientEmail = (headers?: Record<string, string>) => {
    if (!headers) return null;
    const deliveredTo = headers['delivered-to'] || headers['Delivered-To'];
    if (deliveredTo) return deliveredTo;
    const to = headers['to'] || headers['To'];
    if (to) {
      const matches = to.match(/<(.+?)>/);
      return matches ? matches[1] : to;
    }
    return null;
  };

  const getAttachmentKey = (attachmentId: string, messageId?: string) => `${messageId || 'unknown'}:${attachmentId}`;

  const getDriveSaveInput = (attachment: EmailAttachment, messageId?: string) => {
    if (!messageId) return null;

    return {
      emailId: messageId,
      attachmentId: attachment.id,
      filename: attachment.filename,
      mimeType: attachment.mimeType,
      size: attachment.size,
      threadId: email?.threadId,
    };
  };

  const handleDownloadAttachment = async (attachment: EmailAttachment, messageId?: string) => {
    if (!accessToken || !messageId) return;

    const attachmentKey = getAttachmentKey(attachment.id, messageId);
    setDownloadingAttachment(attachmentKey);
    try {
      await downloadAttachmentFromCFWorker(
        messageId,
        attachment.id,
        attachment.filename,
        accessToken,
        attachment.mimeType
      );
      toast.success(`Downloaded ${attachment.filename}`);
    } catch (error) {
      toast.error('Failed to download attachment');
    } finally {
      setDownloadingAttachment(null);
    }
  };

  const handleViewAttachment = async (attachment: EmailAttachment, messageId?: string) => {
    if (!accessToken || !messageId) return;

    const attachmentKey = getAttachmentKey(attachment.id, messageId);
    setViewingAttachment(attachmentKey);
    try {
      await openAttachmentFromCFWorker(messageId, attachment.id, accessToken, attachment.mimeType);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to open attachment preview';
      toast.error(message);
    } finally {
      setViewingAttachment(null);
    }
  };

  const handleSaveAttachmentToDrive = async (attachment: EmailAttachment, messageId?: string) => {
    const saveInput = getDriveSaveInput(attachment, messageId);
    if (!saveInput) return;

    const attachmentKey = getAttachmentKey(attachment.id, messageId);
    const existingSave = savedDriveAttachments[attachmentKey];
    const cachedSave = getCachedSavedDriveAttachment(saveInput);

    if (!user?.driveConnected) {
      const knownDriveLink = existingSave?.webViewLink || cachedSave?.webViewLink;
      if (knownDriveLink) {
        window.open(knownDriveLink, '_blank', 'noopener,noreferrer');
        return;
      }

      startDriveConnectForAttachment(saveInput);
      return;
    }

    setSavingAttachmentKey(attachmentKey);
    try {
      const result = await saveAttachmentToDrive(saveInput);
      cacheSavedDriveAttachment(saveInput, result);
      setSavedDriveAttachments((prev) => ({
        ...prev,
        [attachmentKey]: {
          webViewLink: result.webViewLink ?? null,
        },
      }));

      if (result.alreadySaved && result.webViewLink) {
        window.open(result.webViewLink, '_blank', 'noopener,noreferrer');
        return;
      }

      toast.success(
        result.alreadySaved
          ? `${attachment.filename} is already in Drive`
          : `Saved ${attachment.filename} to Drive`,
        {
          action: result.webViewLink
            ? {
                label: 'Open Drive',
                onClick: () => window.open(result.webViewLink!, '_blank', 'noopener,noreferrer'),
              }
            : undefined,
        }
      );
    } catch (error) {
      if (error instanceof DriveClientError && (error.code === 'DRIVE_NOT_CONNECTED' || error.code === 'DRIVE_REAUTH_REQUIRED')) {
        startDriveConnectForAttachment(saveInput);
        return;
      }

      const message = error instanceof Error ? error.message : 'Failed to save attachment to Drive';
      toast.error(message);
    } finally {
      setSavingAttachmentKey(null);
    }
  };

  const toggleMessageExpand = (messageId: string) => {
    setExpandedMessages(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const labels = email ? getAnalysisLabels(email.aiAnalysis) : [];
  const hasLabels = labels.length > 0 || (email?.userLabels && email.userLabels.length > 0);
  const attachments = email?.attachments ?? EMPTY_ATTACHMENTS;
  const isStarred = Boolean(email?.isStarred);

  useEffect(() => {
    const nextSavedAttachments: Record<string, { webViewLink: string | null }> = {};
    const registerAttachment = (attachment: EmailAttachment, messageId?: string) => {
      const saveInput = getDriveSaveInput(attachment, messageId);
      if (!saveInput) return;

      const cachedSave = getCachedSavedDriveAttachment(saveInput);
      if (!cachedSave) return;

      nextSavedAttachments[getAttachmentKey(attachment.id, messageId)] = {
        webViewLink: cachedSave.webViewLink,
      };
    };

    attachments.forEach((attachment) => registerAttachment(attachment, email?.id));
    thread?.messages.forEach((message) => {
      message.attachments.forEach((attachment) => registerAttachment(attachment, message.id));
    });

    setSavedDriveAttachments((current) => (
      areSavedDriveAttachmentsEqual(current, nextSavedAttachments)
        ? current
        : nextSavedAttachments
    ));
  }, [attachments, email?.id, email?.threadId, thread?.messages]);

  const handleStarClick = async () => {
    if (!email?.id || !onStarToggle) return;
    try {
      await Promise.resolve(onStarToggle(email.id, !isStarred));
    } catch {
      toast.error('Failed to update star status');
    }
  };

  const handleEmailAction = async (action: 'delete' | 'archive' | 'spam' | 'unsubscribe' | 'mark_unread') => {
    if (!email?.id || !accessToken) return;

    setActionInProgress(action);
    try {
      if (action === 'unsubscribe') {
        // Unsubscribe requires two steps:
        // 1) Move email to unsubscribed state in Gmail/DB
        const result = await bulkEmailAction('unsubscribe', [email.id], accessToken);
        const first = result.results?.[0];
        if (!first || first.status !== 'success' || !first.value) {
          throw new Error(first?.error || 'Failed to prepare unsubscribe request');
        }

        const unsubValue = first.value as UnsubscribeValue;
        const unsubResult = await handleUnsubscribe(
          {
            emailId: email.id,
            method: unsubValue.method as 'none' | 'link' | 'post' | 'list-unsubscribe',
            unsubscribeLink: unsubValue.unsubscribeLink,
            formData: unsubValue.formData,
            listUnsubscribeHeader: unsubValue.listUnsubscribeHeader,
            listUnsubscribePost: unsubValue.listUnsubscribePost,
            userEmail: user?.email || '',
          },
          accessToken
        );

        if (!unsubResult.success) {
          throw new Error(unsubResult.message || 'Unsubscribe request failed');
        }

        toast.success(unsubResult.message);
        onEmailAction?.(email.id, 'unsubscribe');
        onClose();
      } else if (action === 'mark_unread') {
        await singleEmailAction(email.id, 'mark_unread', accessToken);
        toast.success('Marked as unread');
        // Don't close modal for mark_unread, just update the UI
      } else {
        await singleEmailAction(email.id, action, accessToken);
        const actionLabels: Record<string, string> = {
          delete: 'Moved to trash',
          archive: 'Archived',
          spam: 'Marked as spam',
        };
        toast.success(actionLabels[action] || 'Action completed');
        onEmailAction?.(email.id, action);
        onClose();
      }
    } catch (error) {
      console.error(`Failed to ${action} email:`, error);
      const message = error instanceof Error ? error.message : `Failed to ${action.replace('_', ' ')} email`;
      toast.error(message || `Failed to ${action.replace('_', ' ')} email`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReplySend = async () => {
    if (!email?.id) return;

    const trimmedReply = replyBody.trim();
    if (!trimmedReply) {
      toast.error('Reply cannot be empty');
      return;
    }

    setIsSendingReply(true);
    try {
      const result = await sendReply(email.id, {
        body: trimmedReply,
        archiveAfterSend: archiveAfterReply,
      });

      if (!archiveAfterReply) {
        const refreshedThreadId = result.threadId || email.threadId;
        if (refreshedThreadId && accessToken) {
          try {
            const refreshedThread = await getEmailThreadFromCFWorker(refreshedThreadId, accessToken);
            if (refreshedThread.messages && refreshedThread.messages.length > 1) {
              setThread(refreshedThread);
              focusLatestThreadMessage(refreshedThread);
            }
          } catch (threadError) {
            console.error('Failed to refresh thread after reply:', threadError);
          }
        }
      }

      resetReplyComposer();

      if (archiveAfterReply) {
        toast.success('Reply sent and email archived');
        onEmailAction?.(email.id, 'archive');
        onClose();
        return;
      }

      toast.success('Reply sent');
    } catch (error) {
      console.error('Failed to send reply:', error);
      const message = error instanceof Error ? error.message : 'Failed to send reply';
      toast.error(message);
    } finally {
      setIsSendingReply(false);
    }
  };

  // Render attachment item
  const renderAttachment = (attachment: EmailAttachment, messageId?: string) => {
    const { icon: FileIcon, className: fileIconClassName } = getAttachmentVisual(attachment);
    const attachmentKey = getAttachmentKey(attachment.id, messageId);
    const isDownloading = downloadingAttachment === attachmentKey;
    const isViewing = viewingAttachment === attachmentKey;
    const isSavingToDrive = savingAttachmentKey === attachmentKey;
    const savedDriveAttachment = savedDriveAttachments[attachmentKey];
    const driveButtonTitle = savedDriveAttachment?.webViewLink
      ? 'Open in Google Drive'
      : savedDriveAttachment
        ? 'Saved to Google Drive'
        : isSavingToDrive
          ? 'Saving to Google Drive'
          : 'Save to Google Drive';

    return (
      <div
        key={attachmentKey}
        className={cn(
          "flex w-full items-center gap-1.5 rounded px-0.5 py-0.5",
          (isDownloading || isSavingToDrive) && "opacity-80"
        )}
      >
        <div className="min-w-0 flex flex-1 items-center gap-1.5">
          <FileIcon className={cn("h-3.5 w-3.5 shrink-0", fileIconClassName)} />
          <div className="min-w-0 flex flex-1 items-baseline gap-1.5 overflow-hidden">
            <span className="truncate text-[10.5px] font-medium leading-tight">{attachment.filename}</span>
            <span className="shrink-0 text-[10px] leading-tight text-muted-foreground tabular-nums">
              {formatFileSize(attachment.size)}
            </span>
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-1">
          <button
            type="button"
            onClick={() => handleViewAttachment(attachment, messageId)}
            disabled={isViewing || !messageId}
            className={cn(
              "inline-flex h-5.5 w-5.5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground",
              (isViewing || !messageId) && "cursor-wait opacity-60"
            )}
            title="View"
          >
            {isViewing ? (
              <span className="h-3 w-3 rounded-full border-2 border-current/30 border-t-current animate-spin" />
            ) : (
              <IconEye className="h-3 w-3" />
            )}
          </button>

          <button
            type="button"
            onClick={() => handleDownloadAttachment(attachment, messageId)}
            disabled={isDownloading || !messageId}
            className={cn(
              "inline-flex h-5.5 w-5.5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground",
              (isDownloading || !messageId) && "cursor-wait opacity-60"
            )}
            title="Download"
          >
            {isDownloading ? (
              <span className="h-3 w-3 rounded-full border-2 border-current/30 border-t-current animate-spin" />
            ) : (
              <IconDownload className="h-3 w-3" />
            )}
          </button>

          <button
            type="button"
            onClick={() => handleSaveAttachmentToDrive(attachment, messageId)}
            disabled={isSavingToDrive || !messageId}
            className={cn(
              "inline-flex h-5.5 w-5.5 items-center justify-center rounded transition-colors hover:bg-muted/70",
              savedDriveAttachment && "bg-emerald-50/80",
              (isSavingToDrive || !messageId) && "cursor-wait opacity-60"
            )}
            title={driveButtonTitle}
          >
            {isSavingToDrive && (
              <span className="h-3 w-3 rounded-full border-2 border-current/30 border-t-current text-muted-foreground animate-spin" />
            )}
            {!isSavingToDrive && <GoogleDriveIcon className="h-3.5 w-3.5 shrink-0" />}
          </button>
        </div>
      </div>
    );
  };

  // Auto-resize iframe to fit content
  const handleThreadIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    const iframe = e.currentTarget;
    resizeIframeToContent(iframe, 140);
    setTimeout(() => resizeIframeToContent(iframe, 140), 100);
  };

  const getPlainTextFromHtml = (html: string) => {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return (doc.body.textContent || '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    } catch {
      return html;
    }
  };

  const buildCopyText = () => {
    if (!email) return '';
    const headerLines = [
      email.subject ? `Subject: ${email.subject}` : 'Subject: (no subject)',
      email.sender ? `From: ${email.sender}` : null,
      getRecipientEmail(email.headers) ? `To: ${getRecipientEmail(email.headers)}` : null,
      email.receivedAt ? `Date: ${new Date(email.receivedAt).toLocaleString()}` : null,
    ].filter(Boolean) as string[];

    if (attachments.length > 0) {
      const attachmentList = attachments
        .map((att) => `${att.filename} (${formatFileSize(att.size)})`)
        .join(', ');
      headerLines.push(`Attachments: ${attachmentList}`);
    }

    const bodyText = thread
      ? thread.messages
          .map((message) => {
            const sender = message.headers.from || message.headers.From || '';
            const recipient = message.headers.to || message.headers.To || '';
            const contentText = message.textBody
              ? message.textBody
              : getPlainTextFromHtml(message.htmlBody || '');
            return [
              `From: ${sender || 'Unknown'}`,
              `To: ${recipient || 'Unknown'}`,
              `Date: ${formatThreadDate(message.internalDate)}`,
              '',
              contentText,
            ]
              .filter(Boolean)
              .join('\n');
          })
          .join('\n\n---\n\n')
      : getPlainTextFromHtml(email.content || '');

    return [headerLines.join('\n'), bodyText].filter(Boolean).join('\n\n');
  };

  const handleCopyEmail = async () => {
    const text = buildCopyText();
    if (!text) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success('Email copied');
    } catch {
      toast.error('Failed to copy email');
    }
  };

  // Render a thread message - compact design for many messages
  const renderThreadMessage = (message: ThreadMessage, isLast: boolean) => {
    const isExpanded = expandedMessages.has(message.id);
    const sender = message.headers.from || message.headers.From || '';
    const recipient = message.headers.to || message.headers.To || '';
    const content = message.htmlBody || message.textBody || '';

    return (
      <div key={message.id} className={cn("border-b border-border/20 last:border-0")}>
        <button
          onClick={() => toggleMessageExpand(message.id)}
          className={cn(
            "w-full flex items-center gap-2 text-left hover:bg-muted/30 rounded px-2 -mx-2",
            isExpanded ? "py-2" : "py-1.5"
          )}
        >
          <IconChevronRight className={cn("h-3 w-3 text-muted-foreground/60 transition-transform shrink-0", isExpanded && "rotate-90")} />
          <div className="shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
            <span className="text-[8px] font-medium text-primary/70">{getSenderInitials(sender)}</span>
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="text-xs font-medium truncate">{getSenderName(sender)}</span>
            {!isExpanded && (
              <span className="text-[10px] text-muted-foreground truncate flex-1">{message.snippet}</span>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-1.5">
            {message.attachments.length > 0 && (
              <IconPaperclip className="h-3 w-3 text-muted-foreground/60" />
            )}
            <span className="text-[10px] text-muted-foreground/60">{formatThreadDate(message.internalDate)}</span>
          </div>
        </button>

        {isExpanded && (
          <div className="mt-1 ml-7 mb-2">
            {/* Sender details when expanded */}
            <div className="text-[10px] text-muted-foreground mb-2">
              {getSenderEmail(sender)} → {getRecipientName(recipient)}
            </div>

            {/* Attachments */}
            {message.attachments.length > 0 && (
              <div className="flex flex-col gap-2 mb-2">
                {message.attachments.map(att => renderAttachment(att, message.id))}
              </div>
            )}

            {/* Content */}
            <iframe
              title={`Message ${message.id}`}
              srcDoc={createIframeSrcDoc(content)}
              className="w-full border-0 rounded bg-muted/10 block overflow-hidden"
              style={{ height: '140px' }}
              onLoad={handleThreadIframeLoad}
              sandbox="allow-same-origin allow-popups"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose();
      }
    }}>
      <DialogContent
        className={cn(
          "max-w-2xl p-0 gap-0 overflow-hidden border-border/50",
          "h-[92vh] max-h-[900px]",
          "flex flex-col"
        )}
      >
        <DialogTitle className="sr-only">
          {email?.subject || 'Email Content'}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Email from {email?.sender}
        </DialogDescription>

        {email ? (
          <>

            {/* Row 1: Subject */}
            <div className="shrink-0 pl-5 pr-12 py-3 border-b border-border/40">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <button
                    onClick={handleStarClick}
                    disabled={!email.id || !onStarToggle}
                    className={cn(
                      "shrink-0 h-5 w-5 flex items-center justify-center rounded transition-colors",
                      isStarred
                        ? "text-yellow-500 hover:text-yellow-600"
                        : "text-muted-foreground/25 hover:text-yellow-500"
                    )}
                    title={isStarred ? "Unstar" : "Star"}
                  >
                    <IconStar className={cn("h-4 w-4", isStarred && "fill-current")} />
                  </button>
                  <h2 className="text-[15px] font-medium text-foreground leading-tight truncate">
                    {email.subject}
                  </h2>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {thread && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {thread.messages.length} messages
                    </span>
                  )}
                  {email.aiAnalysis?.priority === "important" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">
                      !
                    </span>
                  )}
                  {email.aiAnalysis?.priority === "low" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-500/15 text-gray-500 font-medium">
                      ↓
                    </span>
                  )}
                  {email.id && email.aiAnalysis && (
                    <AIAnalysisPopover
                      analysis={email.aiAnalysis}
                      label="AI"
                      className="h-5"
                      gmailMessageId={email.id}
                      onAnalysisUpdate={(analysis) => onAnalysisUpdate?.(email.id!, analysis)}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: Sender */}
            <div className="shrink-0 pl-5 pr-5 py-3 border-b border-border/40 bg-muted/5">
              <div className="flex items-center gap-3">
                <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <span className="text-xs font-medium text-primary/70">{getSenderInitials(email.sender)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">{getSenderName(email.sender)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span className="truncate">{getSenderEmail(email.sender)}</span>
                    <span className="shrink-0">·</span>
                    <button
                      onClick={() => setShowDetails(!showDetails)}
                      className="shrink-0 flex items-center gap-0.5 hover:text-foreground transition-colors"
                    >
                      <span>to {getRecipientEmail(email.headers)?.split('@')[0] || 'me'}</span>
                      <IconChevronDown className={cn("h-3 w-3 transition-transform", showDetails && "rotate-180")} />
                    </button>
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  <span className="text-xs text-muted-foreground mr-1">{email.receivedAt && formatDate(email.receivedAt)}</span>

                  {/* Email Actions */}
                  <div className="flex items-center border-l border-border/40 pl-2 ml-1 gap-0.5">
                    <ActionButton
                      action="archive"
                      title="Archive"
                      icon={IconArchive}
                      onAction={handleEmailAction}
                      disabled={actionInProgress !== null || isSendingReply}
                      actionInProgress={actionInProgress}
                    />
                    <ActionButton
                      action="delete"
                      title="Delete"
                      icon={IconTrash}
                      hoverClassName="hover:text-red-500"
                      onAction={handleEmailAction}
                      disabled={actionInProgress !== null || isSendingReply}
                      actionInProgress={actionInProgress}
                    />
                    <ActionButton
                      action="spam"
                      title="Mark as spam"
                      icon={IconAlertTriangle}
                      hoverClassName="hover:text-orange-500"
                      onAction={handleEmailAction}
                      disabled={actionInProgress !== null || isSendingReply}
                      actionInProgress={actionInProgress}
                    />
                    <ActionButton
                      action="unsubscribe"
                      title="Unsubscribe"
                      icon={IconBellOff}
                      onAction={handleEmailAction}
                      disabled={actionInProgress !== null || isSendingReply}
                      actionInProgress={actionInProgress}
                    />
                    <ActionButton
                      action="mark_unread"
                      title="Mark as unread"
                      icon={IconMail}
                      onAction={handleEmailAction}
                      disabled={actionInProgress !== null || isSendingReply}
                      actionInProgress={actionInProgress}
                    />
                  </div>

                  <div className="flex items-center border-l border-border/40 pl-2 ml-1 gap-0.5">
                    <button
                      onClick={handleCopyEmail}
                      className="p-1.5 rounded hover:bg-muted transition-colors"
                      title="Copy email"
                    >
                      <IconCopy className="h-4 w-4 text-muted-foreground" />
                    </button>
                    {getGmailLink(email) && (
                      <a href={getGmailLink(email)!} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-muted transition-colors" title="Open in Gmail">
                        <IconExternalLink className="h-4 w-4 text-muted-foreground" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Extras (Attachments, Details, Labels) */}
            {(attachments.length > 0 || showDetails || hasLabels) && (
              <div className="shrink-0 px-3 py-1.5 border-b border-border/40 bg-muted/10">
                {attachments.length > 0 && email.id && (
                  <div>
                    <div className="flex flex-col gap-0.5">
                      {attachments.map(att => renderAttachment(att, email.id))}
                    </div>
                  </div>
                )}

                {showDetails && (
                  <div className={cn(attachments.length > 0 && "mt-2 pt-2 border-t border-border/30", "text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1")}>
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground/60">From</span>
                      <span className="text-foreground/80">{email.sender}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground/60">To</span>
                      <span className="text-foreground/80">{getRecipientEmail(email.headers)}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground/60">Date</span>
                      <span className="text-foreground/80">{email.receivedAt && new Date(email.receivedAt).toLocaleString()}</span>
                    </span>
                  </div>
                )}

                {hasLabels && (
                  <div className={cn((attachments.length > 0 || showDetails) && "mt-2 pt-2 border-t border-border/30", "flex items-center gap-1.5 flex-wrap")}>
                    {email.id && labels.filter(label => !email.userLabels?.some(ul => ul.name === label)).map((label, idx) => (
                      <LabelEditor key={`${email.id}-${label}-${idx}`} currentLabel={label} senderEmail={getSenderEmail(email.sender) || ''} emailId={email.id!} onLabelChange={(oldLabel, newLabel) => onLabelChange?.(email.id!, oldLabel, newLabel)} />
                    ))}
                    {email.id && email.userLabels?.map((userLabel) => (
                      <LabelEditor key={userLabel.id} currentLabel={userLabel.name} senderEmail={getSenderEmail(email.sender) || ''} emailId={email.id!} onLabelChange={(oldLabel, newLabel) => onLabelChange?.(email.id!, oldLabel, newLabel)} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-y-auto relative">
              {loadingThread ? (
                <div className="flex items-center justify-center h-32">
                  <div className="h-5 w-5 border-2 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
                </div>
              ) : thread ? (
                /* Thread view */
                <div className="px-5 py-4 space-y-2">
                  {thread.messages.map((msg, idx) => renderThreadMessage(msg, idx === thread.messages.length - 1))}
                </div>
              ) : (
                /* Single message view */
                <>
                  {/* Email body */}
                  {email.content ? (
                    <>
                      {!iframeLoaded && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="h-5 w-5 border-2 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
                        </div>
                      )}
                      <iframe
                        ref={iframeRef}
                        title="Email content"
                        srcDoc={createIframeSrcDoc(email.content)}
                        onLoad={handleIframeLoad}
                        className={cn("w-full border-0 transition-opacity duration-150 block overflow-hidden", iframeLoaded ? "opacity-100" : "opacity-0")}
                        style={{ height: '220px' }}
                        sandbox="allow-same-origin allow-popups"
                      />
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full text-sm text-muted-foreground">No content available</div>
                  )}
                </>
              )}
            </div>

            <div className="shrink-0 border-t border-border/40 bg-muted/10 px-5 py-4">
              {isReplying ? (
                <>
                  <div className="mb-2">
                  <div>
                    <div className="text-xs font-medium text-foreground">Reply</div>
                    <div className="text-[11px] text-muted-foreground">
                      Sends a plain-text reply in the current Gmail thread.
                    </div>
                  </div>
                  </div>

                  <Textarea
                    ref={replyTextareaRef}
                    value={replyBody}
                    onChange={(event) => setReplyBody(event.target.value)}
                    placeholder="Write your reply..."
                    className="min-h-[120px] resize-y bg-background"
                    disabled={isSendingReply}
                  />

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <Checkbox
                        checked={archiveAfterReply}
                        onCheckedChange={(checked) => setArchiveAfterReply(checked === true)}
                        disabled={isSendingReply}
                      />
                      Archive this email after sending
                    </label>

                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={resetReplyComposer}
                        disabled={isSendingReply}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleReplySend}
                        disabled={isSendingReply || replyBody.trim().length === 0}
                        className="gap-1.5"
                      >
                        {isSendingReply && (
                          <span className="h-3.5 w-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                        )}
                        {!isSendingReply && <IconSend className="h-3.5 w-3.5" />}
                        {isSendingReply ? 'Sending...' : 'Send Reply'}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsReplying(true)}
                    disabled={actionInProgress !== null || isSendingReply}
                    className="gap-1.5"
                  >
                    <IconArrowBackUp className="h-3.5 w-3.5" />
                    Reply
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="h-5 w-5 border-2 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
